import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, LogOut } from "lucide-react";
import "./DibrettoPage.css";
import { DibrettoPaneProps } from "../types";
import { config } from "../config";
import { reportFetchError, fetchWithTimeout } from "../lib/fetchError";

// Maximale documentgrootte. Net onder de server-limiet van het /add-in/letters/create
// endpoint (nginx client_max_body_size = 50M); de marge vangt de multipart-overhead op.
// We controleren dit vóór de upload zodat de gebruiker een nette melding krijgt in plaats
// van een mislukte API-call. Mocht het er tóch langs komen, dan vangen we de 413
// hieronder alsnog netjes af.
const MAX_DOCX_BYTES = 45 * 1024 * 1024;

/** Bytes als "12.3 MB" tonen in foutmeldingen. */
const formatMb = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export function DibrettoPage({ token, email, activeLanguages = [] }: DibrettoPaneProps) {
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedLanguage, setSelectedLanguage] = useState<string>(activeLanguages[0]?.key || "");
  const [secureWithPin, setSecureWithPin] = useState<boolean>(true);

  const { t } = useTranslation();

  async function insertImageBase64(base64Image: string) {
    await Word.run(async (context) => {
      const body = context.document.body;

      // Voeg een lege paragraaf toe vóór de afbeelding (voor wat ruimte)
      body.insertParagraph("", Word.InsertLocation.end);

      // Voeg de afbeelding toe, op een aparte regel
      body.insertInlinePictureFromBase64(base64Image, Word.InsertLocation.end);

      await context.sync();
    });
  }

  async function lockDocument() {
    await Word.run(async (context) => {
      // Het ziet er een beetje vreemd uit, maar alleen zo kreeg ik de cursor echt
      // over het hele document heen, dus na de QR afbeelding
      const startRange = context.document.body.getRange(Word.RangeLocation.start);
      startRange.select();
      await context.sync();
      const endRange = context.document.body.getRange(Word.RangeLocation.end);
      endRange.select();
      await context.sync();

      // Doe een content control over de hele body en beveilig het
      const body = context.document.body;
      const cc = body.insertContentControl();
      cc.title = "Door dibretto beveiligd document";
      cc.tag = "beveiligd";
      cc.appearance = "BoundingBox";
      cc.cannotEdit = true;
      cc.cannotDelete = true;
      await context.sync();
    });
  }

  async function getDocxBlob(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed, // levert het documentpakket op als .docx
        (result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            return reject(result.error);
          }
          const file = result.value; // Office.File
          const sliceCount = file.sliceCount;
          const slices: Uint8Array[] = [];
          let received = 0;

          const getSlice = (index: number) => {
            file.getSliceAsync(index, (sliceResult) => {
              if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
                file.closeAsync();
                return reject(sliceResult.error);
              }
              const data = sliceResult.value.data as ArrayBuffer;
              slices.push(new Uint8Array(data));
              received++;

              if (received === sliceCount) {
                file.closeAsync();
                const blob = new Blob(slices, {
                  type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                });
                resolve(blob);
              } else {
                getSlice(index + 1);
              }
            });
          };

          getSlice(0);
        }
      );
    });
  }

  function getSuggestedFilename(): Promise<string> {
    return new Promise((resolve) => {
      Office.context.document.getFilePropertiesAsync((res) => {
        const fallback = "brief.docx";
        if (res.status === Office.AsyncResultStatus.Succeeded && res.value?.url) {
          const url = res.value.url.split("?")[0];
          const last = url.split("/").pop() || fallback;
          resolve(last.endsWith(".docx") ? last : fallback);
        } else {
          resolve(fallback);
        }
      });
    });
  }

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    const url = `${config.apiUrl}/add-in/letters/create`;
    let docxSizeBytes: number | undefined;
    try {
      const blob = await getDocxBlob();
      const filename = await getSuggestedFilename();
      docxSizeBytes = blob.size;

      if (blob.size > MAX_DOCX_BYTES) {
        setError(
          `Dit document is te groot (${formatMb(blob.size)}); het maximum is ${formatMb(MAX_DOCX_BYTES)}. ` +
            "Verklein het document en probeer het opnieuw."
        );
        setIsSaved(false);
        setIsSaving(false);
        return;
      }

      const form = new FormData();
      form.append("file", new File([blob], filename, { type: blob.type }));
      form.append("preferred_language", selectedLanguage || "nl-NL");
      form.append("secureWithPIN", secureWithPin ? "true" : "false");

      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: form,
      });

      if (!response.ok) {
        const body = await response.text();

        // Te grote upload: nginx/PHP geeft een 413. Toon een begrijpelijke melding
        // i.p.v. de rauwe nginx-HTML-body.
        if (response.status === 413) {
          throw new Error(
            `Dit document is te groot om te verwerken${docxSizeBytes ? ` (${formatMb(docxSizeBytes)})` : ""}. ` +
              "Verklein het document en probeer het opnieuw."
          );
        }

        // Overige fouten: gebruik Laravel's JSON-melding als die er is, anders
        // een generieke tekst — nooit de rauwe response-body tonen.
        let message = `Er ging iets mis bij het verwerken (status ${response.status}). Probeer het opnieuw.`;
        try {
          const data = JSON.parse(body);
          if (data?.message) message = data.message;
        } catch {
          // Geen JSON-body (bijv. nginx-HTML) — generieke melding gebruiken.
        }
        throw new Error(message);
      }

      const data = await response.json();
      const qr = data.qr_image.replace(/^data:image\/png;base64,/, "");

      insertImageBase64(qr);
      lockDocument();
      setIsSaved(true);
    } catch (e) {
      setError(reportFetchError(e, url, { docxSizeBytes }));
      setIsSaved(false);
    } finally {
      setIsSaving(false);
    }
  };

  const openWithSso = async (redirectTo: string) => {
    try {
      const response = await fetch(`${config.apiUrl}/add-in/auth/sso-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ redirect_to: redirectTo }),
      });

      if (!response.ok) throw new Error("SSO token request failed");

      const { url } = await response.json();

      if (Office.context.ui.openBrowserWindow) {
        Office.context.ui.openBrowserWindow(url);
      } else {
        window.open(url, "_blank");
      }
    } catch {
      // Fallback: open the app without SSO
      const fallbackUrl = `${config.appUrl}/${redirectTo}`;
      if (Office.context.ui.openBrowserWindow) {
        Office.context.ui.openBrowserWindow(fallbackUrl);
      } else {
        window.open(fallbackUrl, "_blank");
      }
    }
  };

  const handleOpenLearningCentre = () => {
    if (Office.context.ui.openBrowserWindow) {
      Office.context.ui.openBrowserWindow("https://learning.dibretto.com/learning/ua/548");
    } else {
      window.open("https://learning.dibretto.com/learning/ua/548", "_blank");
    }
  };

  const handleLogOut = () => {
    window.location.reload();
  };

  return (
    <div className="dibretto-pane">
      <h3>{t("dibretto.loggedIn")}</h3>
      <p>{email}</p>

      {!isSaved && (
        <>
          <div style={{ marginBottom: "20px" }}>
            <h3>{t("dibretto.pincode.header")}</h3>
            <p>{t("dibretto.pincode.intro")}</p>

            <button
              className="inline service-checkbox"
              onClick={() => setSecureWithPin((v) => !v)}
              type="button"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "flex-start",
                width: "100%",
                paddingTop: "11px",
                paddingBottom: "11px",
              }}
            >
              <input
                type="checkbox"
                checked={secureWithPin}
                onChange={() => setSecureWithPin((v) => !v)}
                onClick={(e) => e.stopPropagation()}
                style={{ margin: 0, cursor: "pointer" }}
              />
              {t("dibretto.pincode.label")}
            </button>

            {!secureWithPin && <div className="pin-alert">{t("dibretto.pincode.warning")}</div>}
          </div>

          <p>{t("dibretto.settings.process.text")}</p>
        </>
      )}

      {isSaved && <p>{t("dibretto.settings.processed.text")}</p>}

      <div className="dibretto-buttons" style={{ marginTop: "10px", marginBottom: "10px" }}>
        {!isSaved && (
          <button onClick={handleSave} disabled={isSaving}>
            {isSaving
              ? t("dibretto.settings.process.buttonLoading")
              : t("dibretto.settings.process.button")}
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <p style={{ fontStyle: "italic" }}>{t("dibretto.settings.mailMergeDisclaimer")}</p>

      <div className="dibretto-buttons">
        <button className="inline" onClick={handleOpenLearningCentre}>
          {t("dibretto.footer.learningCentre")}
          <ExternalLink
            size={16}
            style={{ marginLeft: 6, verticalAlign: "middle", marginTop: -3 }}
          />
        </button>

        <button className="inline" onClick={() => openWithSso("helpdesk")}>
          {t("dibretto.footer.helpdesk")}
          <ExternalLink
            size={16}
            style={{ marginLeft: 6, verticalAlign: "middle", marginTop: -3 }}
          />
        </button>

        <button className="inline" onClick={() => openWithSso("suggestions")}>
          {t("dibretto.footer.suggestImprovement")}
          <ExternalLink
            size={16}
            style={{ marginLeft: 6, verticalAlign: "middle", marginTop: -3 }}
          />
        </button>
      </div>

      <div className="dibretto-buttons" style={{ marginTop: "0" }}>
        <button className="inline" onClick={handleLogOut}>
          {t("dibretto.footer.logout")}
          <LogOut size={16} style={{ marginLeft: 6, verticalAlign: "middle", marginTop: -3 }} />
        </button>
      </div>
    </div>
  );
}
